import { Injectable, NgZone } from '@angular/core';
import { GuardsCheckEnd, NavigationStart, Router } from '@angular/router';
import { cacheBustedUrl, claimReloadSlot } from '../chunk-reload';

/**
 * Moves open tabs onto a new deploy (MP-5600).
 *
 * The deploy stamps index.html with <meta name="app-version" data-built-at>
 * and publishes /version.json (no-store). While the tab is open we poll
 * version.json (every 5 min while visible, on tab focus, and on navigation).
 * When the server has a different build:
 *   - with updateOnNavigation (default), the next in-app navigation, once its
 *     guards have passed, becomes a full page load of the target URL, so the
 *     user lands on the new code without losing anything typed on the page;
 *   - if version.json's minSupportedBuiltAt is newer than this build (a
 *     breaking deploy, set via force-update-token), reload now, unless
 *     skipFullLoad says the current page must not be reloaded; then it
 *     happens at the next eligible navigation.
 *
 * Does nothing when index.html has no app-version meta (local dev builds).
 */
export interface RunningBuild {
  version: string;
  builtAt: number;
  sha: string;
}

export function readRunningBuild(): RunningBuild | null {
  const meta = document.querySelector('meta[name="app-version"]');
  const version = meta?.getAttribute('content');
  if (!version) return null;
  return {
    version,
    builtAt: Number(meta.getAttribute('data-built-at')) || 0,
    sha: meta.getAttribute('data-sha') ?? '',
  };
}

interface ServerVersion {
  version: string;
  builtAt: number;
  minSupportedBuiltAt?: number;
}

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60_000;
const MIN_CHECK_GAP_MS = 60_000;

export interface AppUpdateOptions {
  /**
   * Turn the next in-app navigation into a full page load when a new build
   * is live (default true). Forced updates apply either way.
   */
  updateOnNavigation?: boolean;
  /**
   * Return true to keep the page as it is: for update-on-navigation `url` is
   * the target, for a forced update it is the current URL. Checked in
   * addition to navigations that carry in-memory router state.
   */
  skipFullLoad?: (url: string) => boolean;
}

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly running = readRunningBuild();
  private options: AppUpdateOptions = {};
  private updatePending = false;
  private forcePending = false;
  private lastCheck = 0;
  private started = false;

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone
  ) {}

  start(options: AppUpdateOptions = {}): void {
    if (!this.running || this.started) return;
    this.started = true;
    this.options = options;

    this.zone.runOutsideAngular(() => {
      setInterval(() => {
        if (document.visibilityState === 'visible') this.check(true);
      }, POLL_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.check();
      });
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && !this.updatePending) {
        this.check();
        return;
      }
      // Decide once the navigation's guards have passed, so canDeactivate
      // guards (unsaved changes) get their say first, and use the URL after
      // redirects.
      if (!(event instanceof GuardsCheckEnd) || !event.shouldActivate || !this.updatePending) return;
      if (!(this.options.updateOnNavigation ?? true) && !this.forcePending) return;
      const navigation = this.router.getCurrentNavigation();
      // Navigations that carry in-memory router state would lose it on a
      // full page load; leave those client-side and catch the next one.
      if (navigation?.extras?.state) return;
      if (navigation?.extras?.skipLocationChange) return;
      if (this.options.skipFullLoad?.(event.urlAfterRedirects)) return;
      // At most one automatic page load per 90s (shared with chunk recovery and
      // forced updates). If the full load lands on a still-cached old build,
      // later navigations stay client-side instead of reloading in a loop.
      if (!claimReloadSlot()) return;
      // Carries ?v=, the reload guard when storage is unavailable.
      const target = cacheBustedUrl(new URL(event.urlAfterRedirects, window.location.origin).toString());
      // Back/forward has already moved the history position: replace it rather
      // than push a duplicate entry.
      if (navigation?.trigger === 'popstate') window.location.replace(target);
      else window.location.assign(target);
    });
  }

  private async check(force = false): Promise<void> {
    const now = Date.now();
    if (!force && now - this.lastCheck < MIN_CHECK_GAP_MS) return;
    this.lastCheck = now;
    try {
      const res = await fetch(`${VERSION_URL}?t=${now}`, { cache: 'no-store' });
      if (!res.ok) return;
      const server = (await res.json()) as ServerVersion;
      if (!server?.version || server.version === this.running.version) return;

      this.updatePending = true;
      if (this.running.builtAt > 0 && (server.minSupportedBuiltAt ?? 0) > this.running.builtAt) {
        this.forcePending = true;
        // Pages that must not be reloaded (checkout, unsaved edits) take the
        // forced update at their next eligible navigation instead.
        if (this.options.skipFullLoad?.(window.location.href)) {
          console.info(`[app-update] forced update deferred on this page until the next navigation`);
          return;
        }
        // Same once-per-90s guard as chunk recovery: if the reload lands on a
        // still-cached old index.html, don't reload again in a loop; the next
        // navigation (full load, updatePending) picks up the new build.
        if (!claimReloadSlot()) {
          console.warn(`[app-update] forced reload suppressed (already reloaded in the last 90s)`);
          return;
        }
        console.info(`[app-update] build ${this.running.version} is below the minimum supported; reloading`);
        window.location.replace(cacheBustedUrl());
      }
    } catch {
      // Offline or version.json missing: try again on the next trigger.
    }
  }

  /**
   * Called when an API response indicates a newer backend version (e.g. smtx-version header).
   * Marks update pending so the existing navigation hook reloads onto the new build without
   * interrupting active user reading.
   */
  handleBackendVersionMismatch(): void {
    if (!this.updatePending) {
      this.updatePending = true;
      console.info('[app-update] API version mismatch detected; update queued for next navigation');
    }
  }
}

